import React, { useState } from 'react';

import { SearchableDropdown } from '@/components';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

import AddressAutocomplete, { type PlacePick } from './AddressAutocomplete';

import { useHubOptions, useProviderClientsDropdown } from './pickerHooks';

export type EndpointMode = 'hub' | 'client' | 'manual';

export interface EndpointValues {
  name: string;
  address: string;
  lat: string;
  long: string;
}

export type EndpointField = keyof EndpointValues;

interface AddressEndpointFieldsProps {
  /** For a11y ids + labels. */
  role: 'origin' | 'destination';
  label: string;
  /** Marker/dot color to match the map. */
  color: string;
  /** Modal open — gates the dropdown data hooks. */
  open: boolean;
  values: EndpointValues;
  errors: Partial<Record<EndpointField, string>>;
  /** Single-field change (clears that field's error upstream). */
  onFieldChange: (field: EndpointField, value: string) => void;
  /** Multi-field fill from a picked hub/client/place. */
  onPatch: (patch: Partial<EndpointValues>) => void;
  /**
   * Which source toggles to offer. Defaults to all three. Pass a subset (e.g.
   * `['manual']`) to restrict — the toggle bar hides when only one remains.
   * 4W stops use `['manual']`: the spec models every stop as a plain address
   * (hub attribution is deferred to v2) and the BE stores only address+coords.
   */
  modes?: EndpointMode[];
  /**
   * Unique id prefix for the inputs' `htmlFor` wiring. Defaults to the
   * role-derived `addr-o` / `addr-d`, which is only unique while a form holds
   * one origin and one destination — a multi-stop wizard passes one per stop.
   */
  idPrefix?: string;
  /**
   * When set, every input also gets an `aria-label` of
   * `"{prefix} · {field}"` (e.g. "Stop 2 · nama tempat") so repeated blocks
   * stay distinguishable to a screen reader.
   */
  ariaLabelPrefix?: string;
  /** Example placeholders for the free-text name / address inputs. */
  placeholders?: { name?: string; address?: string };
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';
const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
const coordCls = `${inputCls} font-mono`;

const round6 = (n: number) => String(Number(n.toFixed(6)));

const MODES: { key: EndpointMode; label: string; icon: string }[] = [
  { key: 'hub', label: 'Hub', icon: 'ri-building-2-line' },
  { key: 'client', label: 'Client', icon: 'ri-store-2-line' },
  { key: 'manual', label: 'Custom Address', icon: 'ri-edit-2-line' },
];

const AddressEndpointFields: React.FC<AddressEndpointFieldsProps> = ({
  role,
  label,
  color,
  open,
  values,
  errors,
  onFieldChange,
  onPatch,
  modes,
  idPrefix: idPrefixProp,
  ariaLabelPrefix,
  placeholders,
}) => {
  const availableModes = MODES.filter((m) => !modes || modes.includes(m.key));
  const ariaLabel = (field: string) =>
    ariaLabelPrefix ? `${ariaLabelPrefix} · ${field}` : undefined;
  const [mode, setMode] = useState<EndpointMode>(
    availableModes[0]?.key ?? 'manual'
  );
  const [hubId, setHubId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [clientId, setClientId] = useState('');

  const hubs = useHubOptions(open && mode === 'hub');
  const providers = useProvidersDropdown(open && mode === 'client');
  const clients = useProviderClientsDropdown(
    providerId,
    open && mode === 'client'
  );

  const idPrefix = idPrefixProp ?? `addr-${role === 'origin' ? 'o' : 'd'}`;
  const hasCoords = values.lat.trim() !== '' && values.long.trim() !== '';

  const handlePlace = (place: PlacePick) => {
    onPatch({
      address: place.address,
      lat: round6(place.latitude),
      long: round6(place.longitude),
    });
  };

  const handleHubPick = (next: string) => {
    setHubId(next);
    const hub = hubs.hubById(next);
    if (hub) {
      onPatch({
        name: hub.name,
        address: hub.address ?? '',
        lat: round6(Number(hub.latitude)),
        long: round6(Number(hub.longitude)),
      });
    }
  };

  const handleClientPick = (next: string) => {
    setClientId(next);
    const client = clients.clientById(next);
    if (client) {
      onPatch({
        name: client.name,
        address: client.address ?? '',
        lat: round6(Number(client.latitude)),
        long: round6(Number(client.longitude)),
      });
    }
  };

  return (
    <div className="space-y-3 rounded-gsm border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-700">
          {label}
        </span>
      </div>

      {/* Source mode toggle — hidden when only one source is offered. */}
      {availableModes.length > 1 && (
      <div className="inline-flex w-full items-center gap-1 rounded-gsm border border-gray-200 bg-gray-50 p-1">
        {availableModes.map((m) => {
          const on = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-gsm px-2.5 py-1.5 text-[12px] font-semibold tracking-tight transition-colors ${
                on
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <i className={`${m.icon} text-[13px]`} aria-hidden="true" />
              {m.label}
            </button>
          );
        })}
      </div>
      )}

      {/* ── Hub mode ─────────────────────────────────────────────────────── */}
      {mode === 'hub' && (
        <div>
          <label htmlFor={`${idPrefix}-hub`} className={labelCls}>
            Pilih hub <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            options={hubs.hubs.map((h) => ({
              value: String(h.id),
              label: h.name,
            }))}
            value={hubId}
            selectedLabelFallback={values.name}
            onChange={handleHubPick}
            placeholder="Cari hub…"
            loading={hubs.loading}
            emptyMessage={hubs.error ?? 'Tidak ada hub'}
          />
        </div>
      )}

      {/* ── Client mode (provider → client) ──────────────────────────────── */}
      {mode === 'client' && (
        <div className="space-y-3">
          <div>
            <label htmlFor={`${idPrefix}-provider`} className={labelCls}>
              Provider <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={providers.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={providerId}
              selectedLabelFallback={providers.labelFor(providerId)}
              onChange={(next) => {
                setProviderId(String(next));
                setClientId('');
              }}
              onSearchChange={providers.setSearch}
              disableLocalFilter
              loading={providers.loading}
              hasMore={providers.hasMore}
              onLoadMore={providers.loadMore}
              placeholder="Cari provider…"
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-client`} className={labelCls}>
              Client <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={clients.options.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              value={clientId}
              selectedLabelFallback={values.name}
              onChange={handleClientPick}
              onSearchChange={clients.setSearch}
              disableLocalFilter
              disabled={!providerId}
              loading={clients.loading}
              hasMore={clients.hasMore}
              onLoadMore={clients.loadMore}
              placeholder={
                providerId ? 'Cari client…' : 'Pilih provider dulu'
              }
              emptyMessage={clients.error ?? 'Tidak ada client'}
            />
          </div>
        </div>
      )}

      {/* ── Free text mode ───────────────────────────────────────────────── */}
      {mode === 'manual' && (
        <>
          <div>
            <label htmlFor={`${idPrefix}-name`} className={labelCls}>
              Nama <span className="text-red-500">*</span>
            </label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              value={values.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              placeholder={
                placeholders?.name ??
                (role === 'origin' ? 'Hub Jakarta Pusat' : 'Apotek Sehat Kemang')
              }
              aria-label={ariaLabel('nama tempat')}
              className={inputCls}
            />
            {errors.name && (
              <p className="mt-1 text-[11px] text-red-600">{errors.name}</p>
            )}
          </div>
          <div>
            <label htmlFor={`${idPrefix}-address`} className={labelCls}>
              Alamat <span className="text-red-500">*</span>
              <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
                pilih saran untuk auto-isi koordinat
              </span>
            </label>
            <AddressAutocomplete
              inputId={`${idPrefix}-address`}
              value={values.address}
              onChange={(v) => onFieldChange('address', v)}
              onPick={handlePlace}
              placeholder={
                placeholders?.address ??
                (role === 'origin'
                  ? 'Jl. Hub No. 1, Jakarta'
                  : 'Jl. Kemang Raya No. 10, Jakarta Selatan')
              }
              ariaLabel={ariaLabel('alamat')}
            />
            {errors.address && (
              <p className="mt-1 text-[11px] text-red-600">{errors.address}</p>
            )}
          </div>
        </>
      )}

      {/* Selected summary for hub/client modes. */}
      {mode !== 'manual' && (values.name || values.address) && (
        <div className="rounded-gsm bg-tint px-3 py-2 text-[12px] text-gray-600">
          {values.name && (
            <p className="font-semibold text-gray-900">{values.name}</p>
          )}
          {values.address && (
            <p className="mt-0.5 leading-relaxed">{values.address}</p>
          )}
        </div>
      )}

      {/* Name error surfaces here in hub/client modes (no name input shown). */}
      {mode !== 'manual' && errors.name && (
        <p className="text-[11px] text-red-600">{errors.name}</p>
      )}
      {mode !== 'manual' && errors.address && (
        <p className="text-[11px] text-red-600">{errors.address}</p>
      )}

      {/* Coordinates — always visible + editable so the map/manual tweak works
          uniformly across every mode. */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor={`${idPrefix}-lat`} className={labelCls}>
            Latitude <span className="text-red-500">*</span>
          </label>
          <input
            id={`${idPrefix}-lat`}
            type="number"
            step="0.000001"
            value={values.lat}
            onChange={(e) => onFieldChange('lat', e.target.value)}
            aria-label={ariaLabel('latitude')}
            placeholder={role === 'origin' ? '-6.2' : '-6.26'}
            className={coordCls}
          />
          {errors.lat && (
            <p className="mt-1 text-[11px] text-red-600">{errors.lat}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-lng`} className={labelCls}>
            Longitude <span className="text-red-500">*</span>
          </label>
          <input
            id={`${idPrefix}-lng`}
            type="number"
            step="0.000001"
            value={values.long}
            onChange={(e) => onFieldChange('long', e.target.value)}
            aria-label={ariaLabel('longitude')}
            placeholder={role === 'origin' ? '106.8' : '106.813'}
            className={coordCls}
          />
          {errors.long && (
            <p className="mt-1 text-[11px] text-red-600">{errors.long}</p>
          )}
        </div>
      </div>
      {mode !== 'manual' && !hasCoords && (
        <p className="text-[11px] text-gray-400">
          Pilih {mode === 'hub' ? 'hub' : 'client'} untuk auto-isi koordinat,
          atau geser marker di peta.
        </p>
      )}
    </div>
  );
};

export default AddressEndpointFields;
